/**
 * Speech-to-speech call over GPT-Live (gpt-live-1, WebRTC in the browser) — the experimental second engine
 * for "Lykke presents". Unlike the Realtime call, GPT-Live is full duplex (she listens while she speaks) and has
 * no function tools on the voice side: the app drives the screen and tells her what is on it
 * (session.thinking.append / session.instructions.append), and questions she cannot answer are delegated to
 * the app (session.delegation.created → the app answers with session.commentary.append).
 * GPT-Live has no browser credentials: `exchange` sends our WebRTC offer to the backend, which opens the
 * session with the API key and returns the answer SDP. The route's answer also carries the cowork voice session
 * (voiceSessionId, endsAt): the call heartbeats it, stops at endsAt and ends it on hang-up, errors and page hide
 * (cowork/voice/voiceSession.ts).
 */
import { toast } from 'sonner';
import { keeperFor, voiceOverText, type VoiceMintAnswer, type VoiceOverReason, type VoiceSessionKeeper } from '@/cowork/voice/voiceSession';

export interface LiveCallHandlers {
  onStatus?: (s: 'connecting' | 'connected' | 'speaking' | 'listening' | 'ended' | 'error') => void;
  /** Transcript fragments; `final` fires once a speaker has been quiet for a moment. */
  onTranscript?: (role: 'kai' | 'user', text: string, final: boolean) => void;
  /** Her voice is (or stopped being) audible — measured on the incoming audio, not guessed. */
  onSpeaking?: (speaking: boolean) => void;
  /** The listener is (or stopped) talking, from the input transcript. */
  onUserSpeaking?: (speaking: boolean) => void;
  /** GPT-Live handed a question to the app; answer with `commentary(text, id)`. */
  onDelegation?: (id: string) => void;
  onError?: (message: string) => void;
  /** The voice session stopped the call (its minutes ran out, or the server ended it): the calm line. A toast without it. */
  onTimeUp?: (message: string, reason: VoiceOverReason) => void;
}

/** GPT-Live takes at most 500 tokens per context event; 1 200 characters stays under it even for number-heavy pages. */
export function chunkContext(text: string, max = 1200): string[] {
  const out: string[] = [];
  let rest = text.trim();
  while (rest.length > max) {
    const cut = Math.max(rest.lastIndexOf('\n', max), rest.lastIndexOf('. ', max));
    const at = cut > max * 0.5 ? cut + 1 : max;
    out.push(rest.slice(0, at).trim());
    rest = rest.slice(at).trim();
  }
  if (rest) out.push(rest);
  return out;
}

/**
 * The instant path for short, unambiguous page commands ("page 4", "next", "back one"). Anything longer ("go back to
 * the first slide", "show me the budget") is read by the page-intent model on the backend (live-nav).
 */
export function pageCommand(text: string, index: number): { kind: 'jump'; page: number } | { kind: 'next' } | null {
  const t = text.toLowerCase().replace(/[.!?,]+/g, ' ').replace(/\s+/g, ' ').trim();
  const m = t.match(/\b(?:page|slide|sida)\s+(\d{1,2})\b/);
  if (m) return { kind: 'jump', page: Number(m[1]) - 1 };
  const words = t.split(' ').filter(Boolean).length;
  if (words > 4) return null;
  if (/^(?:ok |okay |yes )?(?:go )?(?:back one(?: slide| page)?|previous(?: page| slide)?|one back|go back)(?: please)?$/.test(t)) return { kind: 'jump', page: index - 1 };
  if (/^(?:ok |okay |yes )?(?:next(?: page| slide)?|go on|continue|carry on|keep going|move on|nästa)(?: please)?$/.test(t)) return { kind: 'next' };
  return null;
}

const TURN_GAP_MS = 1200;      // a speaker quiet this long has finished their turn
const SPEAKING_RMS = 0.012;    // her audio above this is speech
const SILENT_AFTER_MS = 650;   // ...and below it this long is a pause

export class LiveCall {
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private mic: MediaStream | null = null;
  private audioEl: HTMLAudioElement | null = null;
  private audioCtx: AudioContext | null = null;
  private meter: number | null = null;
  private outbox: Array<Record<string, unknown>> = [];
  private ended = false;
  private kaiText = ''; private userText = '';
  private kaiTimer: number | null = null; private userTimer: number | null = null;
  private speaking = false; private quietSince = 0; private heard = false;
  private handlers: LiveCallHandlers;
  private keeper: VoiceSessionKeeper | null = null;

  constructor(handlers: LiveCallHandlers = {}) { this.handlers = handlers; }

  /**
   * `exchange` sends the offer to the backend and returns its answer: the route's JSON (`sdp` plus the voice session
   * fields) or just the answer SDP (older callers).
   */
  async connect(exchange: (offerSdp: string) => Promise<string | (VoiceMintAnswer & { sdp: string })>): Promise<void> {
    this.handlers.onStatus?.('connecting');
    this.mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    if (this.ended) { this.mic.getTracks().forEach((t) => t.stop()); return; }
    const pc = new RTCPeerConnection();
    this.pc = pc;
    this.audioEl = document.createElement('audio');
    this.audioEl.autoplay = true;
    pc.ontrack = (e) => { if (this.audioEl) this.audioEl.srcObject = e.streams[0]; this.watchVoice(e.streams[0]); };
    for (const track of this.mic.getTracks()) pc.addTrack(track, this.mic);
    // The data channel must exist before the offer is made.
    const dc = pc.createDataChannel('oai-events');
    this.dc = dc;
    dc.onmessage = (e) => { try { this.onEvent(JSON.parse(e.data)); } catch { /* ignore */ } };
    dc.onopen = () => {
      const early = this.outbox; this.outbox = [];
      for (const ev of early) this.send(ev);
      this.handlers.onStatus?.('connected');
    };
    // 'disconnected' is often a blip that recovers; only a failed or closed connection ends the call.
    pc.onconnectionstatechange = () => { if (['failed', 'closed'].includes(pc.connectionState) && !this.ended) this.end(); };
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    try {
      const answer = await exchange(offer.sdp || '');
      if (typeof answer !== 'string') {
        this.keeper = keeperFor(answer, (reason) => this.timeUp(reason));
        if (this.ended) { this.keeper?.end(); return; }
      }
      if (this.ended) return;
      await pc.setRemoteDescription({ type: 'answer', sdp: typeof answer === 'string' ? answer : answer.sdp });
      // GPT-Live: the server holds no call id it can hang up, so no callId is reported; the heartbeat keeps the
      // session's minutes honest and `end` settles them.
      this.keeper?.start();
    } catch (e) { this.end(); throw e; }
  }

  private timeUp(reason: VoiceOverReason) {
    if (this.ended) return;
    const message = voiceOverText(reason);
    if (this.handlers.onTimeUp) this.handlers.onTimeUp(message, reason); else toast(message);
    this.end();
  }

  /** The cowork voice session id of this call, once the route answered (for the call archive). */
  get voiceSessionId(): string | null { return this.keeper?.sessionId ?? null; }

  /** Quiet context: what is on screen, what just changed. */
  thinking(text: string) { for (const c of chunkContext(text)) this.send({ type: 'session.thinking.append', delegation_id: null, content: c }); }
  /** A steer she acts on now ("present the page on screen"). */
  instruct(text: string) { for (const c of chunkContext(text)) this.send({ type: 'session.instructions.append', delegation_id: null, content: c }); }
  /** The answer to a delegated question — she says it. */
  commentary(text: string, delegationId: string) { for (const c of chunkContext(text)) this.send({ type: 'session.commentary.append', delegation_id: delegationId, content: c }); }

  setMuted(muted: boolean) { this.mic?.getTracks().forEach((t) => { t.enabled = !muted; }); }
  /** Call from a click/key handler: lets a suspended audio meter start. */
  resumeAudio() { if (this.audioCtx?.state === 'suspended') void this.audioCtx.resume().catch(() => {}); }
  /** The meter has measured her voice at least once (else the app falls back to the transcript). */
  get meterWorks() { return this.heard; }
  /** Finish the listener's turn now (a delegation arrives before the transcript settles) and return it. */
  flushUser(): string {
    if (this.userTimer) { window.clearTimeout(this.userTimer); this.userTimer = null; }
    const t = this.userText.trim(); this.userText = '';
    if (t) { this.handlers.onTranscript?.('user', t, true); this.handlers.onUserSpeaking?.(false); }
    return t;
  }
  get active() { return !this.ended; }
  get isSpeaking() { return this.speaking; }

  private send(ev: Record<string, unknown>) {
    if (this.ended) return;
    if (this.dc && this.dc.readyState === 'open') this.dc.send(JSON.stringify(ev));
    else this.outbox.push(ev);
  }

  /** WebRTC carries no playback events: measure her audio level to know when she speaks and when she has stopped. */
  private watchVoice(stream: MediaStream) {
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctx();
      // Created after network awaits (outside the click): browsers may start it suspended, which reads silence.
      void this.audioCtx.resume().catch(() => {});
      const analyser = this.audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      this.audioCtx.createMediaStreamSource(stream).connect(analyser);
      const buf = new Float32Array(analyser.fftSize);
      this.meter = window.setInterval(() => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0; for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const loud = Math.sqrt(sum / buf.length) > SPEAKING_RMS;
        const now = Date.now();
        if (loud) { this.quietSince = 0; this.heard = true; if (!this.speaking) { this.speaking = true; this.handlers.onSpeaking?.(true); this.handlers.onStatus?.('speaking'); } }
        else if (this.speaking) {
          if (!this.quietSince) this.quietSince = now;
          else if (now - this.quietSince > SILENT_AFTER_MS) { this.speaking = false; this.handlers.onSpeaking?.(false); this.handlers.onStatus?.('listening'); }
        }
      }, 50);
    } catch { /* no meter: the transcript still drives the captions */ }
  }

  private onEvent(ev: any) {
    switch (ev.type) {
      case 'session.output_transcript.delta': {
        this.kaiText += String(ev.delta || '');
        this.handlers.onTranscript?.('kai', this.kaiText.trim(), false);
        if (this.kaiTimer) window.clearTimeout(this.kaiTimer);
        this.kaiTimer = window.setTimeout(() => { const t = this.kaiText.trim(); this.kaiText = ''; if (t) this.handlers.onTranscript?.('kai', t, true); }, TURN_GAP_MS);
        break;
      }
      case 'session.input_transcript.delta': {
        if (!this.userText) this.handlers.onUserSpeaking?.(true);
        this.userText += String(ev.delta || '');
        this.handlers.onTranscript?.('user', this.userText.trim(), false);
        if (this.userTimer) window.clearTimeout(this.userTimer);
        this.userTimer = window.setTimeout(() => { const t = this.userText.trim(); this.userText = ''; if (t) this.handlers.onTranscript?.('user', t, true); this.handlers.onUserSpeaking?.(false); }, TURN_GAP_MS);
        break;
      }
      case 'session.delegation.created':
        if (ev.delegation?.target === 'client' && ev.delegation?.id) this.handlers.onDelegation?.(String(ev.delegation.id));
        break;
      case 'session.closed': this.end(); break;
      case 'error':
        console.warn('[gpt-live] error', ev.error);
        this.handlers.onError?.(ev.error?.message || 'GPT-Live error');
        break;
      default: break;
    }
  }

  end() {
    if (this.ended) return;
    try { if (this.dc?.readyState === 'open') this.dc.send(JSON.stringify({ type: 'session.close' })); } catch { /* noop */ }
    this.ended = true;
    this.keeper?.end();
    if (this.meter) window.clearInterval(this.meter);
    for (const t of [this.kaiTimer, this.userTimer]) if (t) window.clearTimeout(t);
    try { void this.audioCtx?.close(); } catch { /* noop */ }
    window.setTimeout(() => { try { this.dc?.close(); } catch { /* noop */ } try { this.pc?.close(); } catch { /* noop */ } }, 300);
    try { this.mic?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    if (this.audioEl) { this.audioEl.srcObject = null; this.audioEl = null; }
    this.handlers.onStatus?.('ended');
  }
}
